import React from 'react';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  Stack,
  Divider
} from '@mui/material';
import {
  ExpandMore,
  Warning,
  CheckCircle,
  Person,
  VerifiedUser,
  Work,
  Schedule,
  Settings,
  Payment,
  RadioButtonUnchecked,
  CheckBoxOutlined
} from '@mui/icons-material';
import { analyzeProfileCompletion, getCompletionSummary } from '../utils/ProfileCompletionTracker';

const SECTION_ICONS = {
  personalInfo: Person,
  legalInsurance: VerifiedUser,
  experience: Work,
  availability: Schedule,
  preferences: Settings,
  payment: Payment
};

const MissingFieldsPanel = ({ maidProfile, onClose }) => {
  const analysisResults = analyzeProfileCompletion(maidProfile);
  const completionSummary = getCompletionSummary(analysisResults);
  
  if (!analysisResults) {
    return (
      <Alert severity="info">
        Unable to analyze profile completion. Please check the profile data.
      </Alert>
    );
  }

  const { overall, sections, allMissingRequired, allMissingOptional } = analysisResults;

  return (
    <Box>
      {/* Overall Status Header */}
      <Paper elevation={2} sx={{ p: 3, mb: 3, backgroundColor: 'primary.light' }}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Box>
            <Typography variant="h6" fontWeight={600}>
              Profile Completion Status
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {overall.completedSections} of {overall.totalSections} sections complete
            </Typography>
          </Box>
          <Box sx={{ flex: 1, mx: 3 }}>
            <LinearProgress
              variant="determinate"
              value={overall.completionPercentage}
              sx={{ height: 8, borderRadius: 4 }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              {overall.completionPercentage}% complete
            </Typography>
          </Box>
          <Chip
            icon={completionSummary.icon === 'CheckCircle' ? <CheckCircle /> : <Warning />}
            label={completionSummary.message}
            color={completionSummary.color}
            variant="outlined"
          />
        </Stack>
      </Paper>

      {/* Missing Required Fields Alert */}
      {allMissingRequired.length > 0 && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <Typography variant="body2" fontWeight={600}>
            {allMissingRequired.length} required field{allMissingRequired.length === 1 ? '' : 's'} missing
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Complete these fields to activate the profile and enable job assignments.
          </Typography>
        </Alert>
      )}

      {/* Section-by-Section Breakdown */}
      <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 2 }}>
        Section Breakdown
      </Typography>

      {Object.entries(sections).map(([sectionKey, sectionData]) => {
        const SectionIcon = SECTION_ICONS[sectionKey] || Settings;
        const hasRequiredMissing = sectionData.missingRequired.length > 0;
        const hasOptionalMissing = sectionData.missingOptional.length > 0;

        return (
          <Accordion 
            key={sectionKey}
            defaultExpanded={hasRequiredMissing}
            sx={{ mb: 1 }}
          >
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Stack direction="row" alignItems="center" spacing={2} sx={{ width: '100%' }}>
                <SectionIcon color={sectionData.isComplete ? 'success' : 'error'} />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle1" fontWeight={500}>
                    {sectionData.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {sectionData.completedRequired} of {sectionData.totalRequired} required • {sectionData.completionPercentage}% complete
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LinearProgress
                    variant="determinate"
                    value={sectionData.completionPercentage}
                    sx={{ width: 60, height: 6, borderRadius: 3 }}
                  />
                  {sectionData.isComplete ? (
                    <CheckCircle color="success" fontSize="small" />
                  ) : (
                    <Warning color="error" fontSize="small" />
                  )}
                </Box>
              </Stack>
            </AccordionSummary>
            
            <AccordionDetails>
              {/* Missing Required Fields */}
              {hasRequiredMissing && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="error" gutterBottom>
                    Missing Required Fields ({sectionData.missingRequired.length})
                  </Typography>
                  <List dense>
                    {sectionData.missingRequired.map((field, index) => (
                      <ListItem key={index} sx={{ py: 0.5 }}>
                        <ListItemIcon>
                          <RadioButtonUnchecked color="error" fontSize="small" />
                        </ListItemIcon>
                        <ListItemText
                          primary={field.label}
                          secondary={field.path}
                          primaryTypographyProps={{ variant: 'body2' }}
                          secondaryTypographyProps={{ variant: 'caption' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

              {/* Missing Optional Fields */}
              {hasOptionalMissing && (
                <Box>
                  <Typography variant="subtitle2" color="warning" gutterBottom>
                    Missing Optional Fields ({sectionData.missingOptional.length})
                  </Typography>
                  <List dense>
                    {sectionData.missingOptional.map((field, index) => (
                      <ListItem key={index} sx={{ py: 0.5 }}>
                        <ListItemIcon>
                          <RadioButtonUnchecked color="warning" fontSize="small" />
                        </ListItemIcon>
                        <ListItemText
                          primary={field.label}
                          secondary={field.path}
                          primaryTypographyProps={{ variant: 'body2' }}
                          secondaryTypographyProps={{ variant: 'caption' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

              {/* Completed Fields */}
              {sectionData.completedRequired > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" color="success" gutterBottom>
                    Completed Fields ({sectionData.completedRequired + sectionData.completedOptional})
                  </Typography>
                  <List dense>
                    {[...sectionData.requiredFields, ...sectionData.optionalFields, ...sectionData.conditionalFields]
                      .filter(field => field.isComplete)
                      .map((field, index) => (
                        <ListItem key={index} sx={{ py: 0.5 }}>
                          <ListItemIcon>
                            <CheckBoxOutlined color="success" fontSize="small" />
                          </ListItemIcon>
                          <ListItemText
                            primary={field.name}
                            secondary={field.isRequired ? 'Required' : 'Optional'}
                            primaryTypographyProps={{ variant: 'body2' }}
                            secondaryTypographyProps={{ variant: 'caption' }}
                          />
                        </ListItem>
                      ))}
                  </List>
                </Box>
              )}

              {/* Section is Complete */}
              {sectionData.isComplete && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    <CheckCircle sx={{ verticalAlign: 'middle', mr: 1 }} />
                    This section is complete! All required fields have been filled.
                  </Typography>
                </Alert>
              )}
            </AccordionDetails>
          </Accordion>
        );
      })}

      {/* Summary Actions */}
      <Box sx={{ mt: 3, p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
        <Typography variant="subtitle2" gutterBottom>
          Next Steps
        </Typography>
        {overall.isComplete ? (
          <Typography variant="body2" color="success.main">
            ✅ Profile is complete and ready for job assignments!
          </Typography>
        ) : allMissingRequired.length === 0 ? (
          <Typography variant="body2" color="warning.main">
            ⏳ All required fields are complete. Profile is pending admin review.
          </Typography>
        ) : (
          <Typography variant="body2" color="error.main">
            ❌ Complete the {allMissingRequired.length} missing required field{allMissingRequired.length === 1 ? '' : 's'} above to activate this profile.
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default MissingFieldsPanel;